package kz.dentalcrm.app.screens.protocols.edit

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.BottomSheetDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.FilePlus
import compose.icons.feathericons.FileText
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.code
import dentalcrm.composeapp.generated.resources.delete
import dentalcrm.composeapp.generated.resources.diagnosis_edit_title
import dentalcrm.composeapp.generated.resources.edit
import dentalcrm.composeapp.generated.resources.protocol_edit_title
import dentalcrm.composeapp.generated.resources.protocol_title_edit_title
import dentalcrm.composeapp.generated.resources.service_naming
import kz.dentalcrm.app.core.ui.UiStateScaffold
import kz.dentalcrm.app.core.ui.components.UIButton
import kz.dentalcrm.app.core.ui.components.UITextInput
import kz.dentalcrm.app.data.network.response_model.protocol_templates.ProtocolTemplate
import kz.dentalcrm.app.screens.protocols.edit.mvi.EditProtocolViewEvent
import kz.dentalcrm.app.screens.protocols.edit.mvi.EditProtocolViewState
import kz.dentalcrm.app.theme.DefaultTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EditProtocolDialog(
    item: ProtocolTemplate,
    onEdit: () -> Unit,
    onDelete: (Int) -> Unit,
    onDismiss: () -> Unit,
) {
    val modalBottomSheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    val screenModel = koinViewModel<EditProtocolViewModel>()
    val state by screenModel.viewState.collectAsState()

    LaunchedEffect(state.screenState) {
        when (state.screenState) {
            is EditProtocolViewState.ScreenState.Edited -> {
                screenModel.handleEvents(EditProtocolViewEvent.Reset)
                onEdit()
                onDismiss()
            }
            is EditProtocolViewState.ScreenState.Deleted -> {
                screenModel.handleEvents(EditProtocolViewEvent.Reset)
                onDelete(item.id)
                onDismiss()
            }
            else -> Unit
        }
    }

    ModalBottomSheet(
        modifier = Modifier.widthIn(max = 450.dp)
            .imePadding(),
        onDismissRequest = {
            screenModel.handleEvents(EditProtocolViewEvent.Reset)
            onDismiss()
        },
        sheetState = modalBottomSheetState,
        dragHandle = { BottomSheetDefaults.DragHandle() },
        containerColor = DefaultTheme.colors.surfaceSecondary,
        content = {
            UiStateScaffold(
                isLoading = state.screenState is EditProtocolViewState.ScreenState.Loading,
                modifier = Modifier.background(Color.Transparent),
                content = {
                    EditServiceContent(
                        protocol = item,
                        state = state,
                        event = screenModel::handleEvents
                    )
                }
            )
        }
    )
}

@Composable
private fun EditServiceContent(
    protocol: ProtocolTemplate,
    state: EditProtocolViewState,
    event: (EditProtocolViewEvent) -> Unit,
) {
    Column(
        modifier = Modifier
            .background(
                color = DefaultTheme.colors.surfaceSecondary,
                shape = RoundedCornerShape(topStart = 16.dp, topEnd = 16.dp)
            )
            .statusBarsPadding()
            .fillMaxWidth(),
    ) {
        var name by remember { mutableStateOf(protocol.name) }
        var code by remember { mutableStateOf(protocol.code) }
        var id by remember { mutableStateOf(protocol.id) }

        Text(
            modifier = Modifier.padding(horizontal = 16.dp),
            text = stringResource(Res.string.protocol_title_edit_title),
            style = DefaultTheme.typography.bodyLargeMedium,
            color = DefaultTheme.colors.onSurface
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        HorizontalDivider(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
            thickness = 1.dp,
            color = DefaultTheme.colors.onSurfaceVariant.copy(
                alpha = 0.2f
            )
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            defaultValue = protocol.name ?: "",
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Text
            ),
            singleLine = false,
            leadingIcon = FeatherIcons.FilePlus,
            label = stringResource(Res.string.service_naming),
        ) {
            name = it
        }
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Text
            ),
            singleLine = false,
            defaultValue = protocol.code ?: "",
            leadingIcon = FeatherIcons.FileText,
            label = stringResource(Res.string.code),
        ) {
            code = it
        }
        Spacer(modifier = Modifier.fillMaxWidth().height(24.dp))
        Row(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            horizontalArrangement = Arrangement.Center
        ) {
            UIButton(
                modifier = Modifier.weight(1f),
                containerColor = DefaultTheme.colors.error,
                text = stringResource(Res.string.delete),
                isLoading = state.screenState is EditProtocolViewState.ScreenState.Editing,
                onClick = {
                    event(
                        EditProtocolViewEvent.DeleteProtocol(
                            treatment = protocol
                        )
                    )
                },
            )
            Spacer(modifier = Modifier.width(8.dp))
            UIButton(
                modifier = Modifier.weight(1f),
                enabled = name.isNullOrEmpty().not() && code.isNullOrEmpty().not(),
                text = stringResource(Res.string.edit),
                isLoading = state.screenState is EditProtocolViewState.ScreenState.Editing,
                onClick = {
                    event(
                        EditProtocolViewEvent.EditProtocol(
                            naming = name.orEmpty(),
                            code = code.orEmpty(),
                            id = id
                        )
                    )
                },
            )
        }

        Spacer(modifier = Modifier.fillMaxWidth().height(32.dp).navigationBarsPadding())
    }
}